import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { SearchOutlined } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { ApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { SystemIcon } from "components/Icons/Icons";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { enqueueSnackbar } from "notistack";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import {
  getPresignedUrl,
  isValidSummaryPayload,
  lowercaseKeys,
  matchesReinsuranceYear,
  normalizeFiles,
  normalizeSummary,
} from "./aipPassHelpers";

const TAB_AGENCIES = 0;
const TAB_PRODUCERS = 1;
const TAB_FILES = 2;

const parseSummaryFromReadmeFiles = async (readmeFiles, globalState) => {
  const parsed = await Promise.all(
    readmeFiles.map(async (f) => {
      try {
        const linkResult = await ApiHelper.callPost("aip-pass/files/FileGetDocumentLink", { key: f.key }, globalState);
        const url = getPresignedUrl(linkResult);
        if (!url) return null;

        const response = await fetch(url);
        if (!response.ok) return null;

        return await response.json();
      } catch {
        return null;
      }
    })
  );

  const allAgencies = [];
  const allProducers = [];
  let server;
  let generatedAt;

  for (const readme of parsed) {
    if (!readme) continue;
    const aip = lowercaseKeys(readme?.aip ?? readme ?? {});
    if (!server) server = aip.server;
    if (!generatedAt) generatedAt = aip.generatedat ?? aip.generated_at;
    for (const a of aip.agencies ?? aip["aip.agencies"] ?? []) {
      allAgencies.push(a);
    }
    for (const p of aip.producers ?? aip["aip.producers"] ?? []) {
      allProducers.push(p);
    }
  }

  return normalizeSummary({
    server,
    generatedAt,
    agencies: allAgencies,
    producers: allProducers,
  });
};

const hasSummaryData = (summary) => (summary?.agencies?.length ?? 0) > 0 || (summary?.producers?.length ?? 0) > 0;

export default function AipPassServerWorkspace() {
  const { vendor } = useParams();
  const { globalState, globalDispatch } = useContext(AppContext);
  const { addAttachments } = useChatAssistantAttachments();

  const [summary, setSummary] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year ?? getCurrentReinsuranceYear());
  const [tab, setTab] = useState(TAB_AGENCIES);
  const [agencySearch, setAgencySearch] = useState("");
  const [producerSearch, setProducerSearch] = useState("");
  const [fileSearch, setFileSearch] = useState("");

  const vendorName = decodeURIComponent(vendor ?? "");

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear, globalDispatch]);

  const loadData = useCallback(async () => {
    setIsLoadingFiles(true);
    setIsLoadingSummary(true);

    try {
      const rawFiles = (await ApiHelper.callPost("aip-pass/files/search", { vendor: vendorName }, globalState)) ?? [];
      const allFiles = normalizeFiles(rawFiles);
      setFiles(allFiles);
      setIsLoadingFiles(false);

      let nextSummary = null;

      try {
        const summaryResult = await ApiHelper.callPost(
          "aip-pass/vendor/summary/search",
          { vendor: vendorName },
          globalState
        );
        if (summaryResult && isValidSummaryPayload(summaryResult)) {
          nextSummary = normalizeSummary(summaryResult);
        }
      } catch {
        // Fall back to README.json when summary API is unavailable.
      }

      const readmeFiles = allFiles.filter((f) => f.key.toLowerCase().endsWith("readme.json"));

      if (!hasSummaryData(nextSummary)) {
        if (readmeFiles.length) {
          nextSummary = await parseSummaryFromReadmeFiles(readmeFiles, globalState);
        } else if (!nextSummary) {
          nextSummary = { server: vendorName, generatedAt: null, agencies: [], producers: [] };
        }
      }

      setSummary(nextSummary);
    } catch (err) {
      enqueueSnackbar(err?.message ?? "Failed to load vendor data.", { variant: "error", preventDuplicate: true });
    } finally {
      setIsLoadingFiles(false);
      setIsLoadingSummary(false);
    }
  }, [vendorName, globalState]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownload = useCallback(
    async (key) => {
      try {
        const result = await ApiHelper.callPost("aip-pass/files/FileGetDocumentLink", { key }, globalState);
        const url = getPresignedUrl(result);
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        enqueueSnackbar(err?.message ?? "Failed to generate download link.", {
          variant: "error",
          preventDuplicate: true,
        });
      }
    },
    [globalState]
  );

  const agencies = useMemo(() => summary?.agencies ?? [], [summary]);
  const producers = useMemo(() => summary?.producers ?? [], [summary]);
  const producersForYear = useMemo(
    () => producers.filter((producer) => matchesReinsuranceYear(producer, currentYear)),
    [producers, currentYear]
  );
  const generatedAt = summary?.generatedAt;
  const serverName = summary?.server ?? vendorName;

  const filteredAgencies = useMemo(() => {
    if (!agencySearch) return agencies;
    const lower = agencySearch.toLowerCase();
    return agencies.filter(
      (a) =>
        (a.agencyName ?? "").toLowerCase().includes(lower) ||
        (a.agencyKey ?? "").toString().includes(lower) ||
        (a.email ?? "").toLowerCase().includes(lower)
    );
  }, [agencies, agencySearch]);

  const filteredProducers = useMemo(() => {
    if (!producerSearch) return producersForYear;
    const lower = producerSearch.toLowerCase();
    return producersForYear.filter(
      (p) =>
        (p.name ?? "").toLowerCase().includes(lower) ||
        (p.producerKey ?? "").toString().includes(lower) ||
        (p.policyNumber ?? "").toString().includes(lower)
    );
  }, [producersForYear, producerSearch]);

  const filteredFiles = useMemo(() => {
    if (!fileSearch) return files;
    const lower = fileSearch.toLowerCase();
    return files.filter((f) => (f.key ?? "").toLowerCase().includes(lower));
  }, [files, fileSearch]);

  const fileRows = useMemo(() => filteredFiles, [filteredFiles]);

  useEffect(() => {
    addAttachments([
      { name: `AIP PASS ${vendorName} Agencies`, content: filteredAgencies },
      { name: `AIP PASS ${vendorName} Producers (${currentYear})`, content: filteredProducers },
    ]);
  }, [filteredAgencies, filteredProducers, vendorName, currentYear, addAttachments]);

  const agencyColumns = useMemo(
    () => [
      { field: "agencyKey", headerName: "Agency Key", width: 120 },
      { field: "agentKey", headerName: "Agent Key", width: 120 },
      { field: "agencyName", headerName: "Agency Name", flex: 1, minWidth: 180 },
      { field: "taxId", headerName: "Tax ID", width: 130 },
      { field: "email", headerName: "Email", flex: 1, minWidth: 160 },
      { field: "fileCount", headerName: "Files", width: 80, type: "number" },
    ],
    []
  );

  const producerColumns = useMemo(
    () => [
      { field: "producerKey", headerName: "Producer Key", width: 130 },
      { field: "policyNumber", headerName: "Policy #", width: 130 },
      { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
      { field: "reinsuranceYear", headerName: "Reinsurance Year", width: 160, type: "number" },
    ],
    []
  );

  const fileColumns = useMemo(
    () => [
      {
        field: "key",
        headerName: "File",
        flex: 1,
        minWidth: 220,
        valueGetter: (value, row) => {
          const k = row?.key ?? "";
          const parts = k.split("/");
          return parts[parts.length - 1] || k;
        },
      },
      {
        field: "size",
        headerName: "Size",
        width: 110,
        valueGetter: (value, row) => {
          const bytes = row?.size ?? 0;
          if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
          if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
          return `${bytes} B`;
        },
      },
      {
        field: "lastModified",
        headerName: "Last Modified",
        width: 160,
        valueGetter: (value, row) => (row?.lastModified ? new Date(row.lastModified).toLocaleString() : ""),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Download",
        headerAlign: "right",
        align: "right",
        width: 90,
        getActions: (params) => [
          <GridActionsCellItem
            key="download"
            icon={
              <Tooltip title="Download">
                <DownloadIcon />
              </Tooltip>
            }
            label="Download"
            onClick={() => handleDownload(params.row.key)}
          />,
        ],
      },
    ],
    [handleDownload]
  );

  return (
    <>
      <WorkspaceHeader
        title={serverName}
        category={{
          title: "System",
          icon: <SystemIcon />,
        }}
        breadCrumbs={[{ title: "AIP PASS", path: "/system/aip-pass" }]}
      />

      <WorkspacePageHeader
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        isLoading={isLoadingSummary || isLoadingFiles}
      />

      {generatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          Last imported: {new Date(generatedAt).toLocaleString()}
        </Typography>
      )}

      {summary && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item>
            <Chip label={`${agencies.length} Agencies`} size="small" variant="outlined" />
          </Grid>
          <Grid item>
            <Chip label={`${producersForYear.length} Producers (${currentYear})`} size="small" variant="outlined" />
          </Grid>
          <Grid item>
            <Chip label={`${files.length} Files`} size="small" variant="outlined" />
          </Grid>
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Agencies (${agencies.length})`} />
        <Tab label={`Producers (${producersForYear.length})`} />
        <Tab label={`Files (${files.length})`} />
      </Tabs>

      {tab === TAB_AGENCIES && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search agencies..."
                value={agencySearch}
                onChange={(e) => setAgencySearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <SearchOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ height: 600, width: "100%" }}>
            <TilleyDataGrid
              rows={filteredAgencies}
              columns={agencyColumns}
              pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
              defaultPageSize={10}
              getRowId={(r) => r.id}
              isLoading={isLoadingSummary}
            />
          </Box>
        </>
      )}

      {tab === TAB_PRODUCERS && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search producers..."
                value={producerSearch}
                onChange={(e) => setProducerSearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <SearchOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ height: 600, width: "100%" }}>
            <TilleyDataGrid
              rows={filteredProducers}
              columns={producerColumns}
              pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
              defaultPageSize={10}
              getRowId={(r) => r.id}
              isLoading={isLoadingSummary}
            />
          </Box>
        </>
      )}

      {tab === TAB_FILES && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search files..."
                value={fileSearch}
                onChange={(e) => setFileSearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <SearchOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ height: 600, width: "100%" }}>
            <TilleyDataGrid
              rows={fileRows}
              columns={fileColumns}
              pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
              defaultPageSize={10}
              getRowId={(r) => r.id}
              isLoading={isLoadingFiles}
            />
          </Box>
        </>
      )}
    </>
  );
}
